import React from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { BaseService, IMember, IMicroTournamentEdition, ITournament, ITournamentEdition, ITournamentEditionWebsite, OmneHostService, SanitizedResponse } from "../../../../services";
import LanguageDropdown from "../../../../shared/LanguageDropdown";
import { useComponentStyle } from "../../../../hooks/useComponentStyle";
import { CacheManager, Utils } from "../../../../utils";
import { Classname, DateHelper, StringHelper, useSearchParams } from "@rivtn/noseur";
import { i18nManager } from "../../../../i18n";
import Events from "./Events";
import Gallery from "./Gallery";
import Schedule from "./Schedule";
import About from "./About";
import Participants from "./Participants";
import Leaderboard from "./Leaderboard";
import Trophies from "./Trophies";

interface EditionSwitcherProps {
    tournamentId: string;
    current: IMicroTournamentEdition;
}

function EditionSwitcher({ tournamentId, current }: EditionSwitcherProps) {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const ref = React.useRef<HTMLDivElement>(null);
    const omnehostService = OmneHostService.getInstance();
    const [tournamentEditions, setTournamentEditions] = React.useState<ITournamentEdition[]>();
    const { edi, setEdi } = useSearchParams({ defaultInit: window.location.search, params: { edi: "" } });

    React.useEffect(() => {
        const h = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", h);

        return () => document.removeEventListener("mousedown", h);
    }, []);

    React.useEffect(() => {
        searchTournamentEditions(search);
    }, [search]);

    React.useEffect(() => {
        if (!edi || edi === current.external_id) return;
        window.location.reload();
    }, [edi]);

    return (
        <div className={styles["dropdown-wrap"]} ref={ref}>
            <button className={styles["edition-btn"]} onClick={() => setOpen(v => !v)}>
                <span>{current.name}</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{new Date(current.created_at).getFullYear()}</span>
                <span className={`${styles["edition-caret"]} ${open ? styles["open"] : ""}`}>▾</span>
            </button>

            {open && (
                <div className={`${styles["dropdown"]} ${styles["edition-dd"]}`}>
                    <input
                        className={styles["ed-search"]}
                        placeholder="Search edition..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        autoFocus
                    />
                    {tournamentEditions?.map((ed) => (
                        <div
                            key={ed.external_id}
                            className={`${styles["ed-item"]} ${ed.external_id === current.external_id ? styles["ed-active"] : ""}`}
                            onClick={() => {
                                setEdi(ed.external_id);
                            }}
                        >
                            <div className={`${styles["ed-dot"]} ${ed.status === "ONGOING" ? styles["ed-dot-active"] : styles["ed-dot-past"]}`} />
                            <div style={{ flex: 1 }}>
                                <div className={styles["ed-name"]}>{ed.name}</div>
                                <div className={styles["ed-meta"]}>{new Date(ed.created_at).getFullYear()}{ed.meta.price_pool_monetary || ed.meta.price_pool_physical ? " · Prized" : ""}{ed.location ? ` · ${ed.location}` : ""}</div>
                            </div>
                            {ed.status === "ONGOING" && (
                                <span style={{ fontSize: "0.62rem", color: "var(--green)", fontWeight: 700, letterSpacing: "1px" }}>LIVE</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    function searchTournamentEditions(name: string = "") {
        if (search) {
            const cached = tournamentEditions?.filter((t) => t.name?.toLowerCase().includes(search.toLocaleLowerCase()));
            if (cached?.length) {
                setTournamentEditions(cached);
                return;
            }
        }
        omnehostService.queryTournamentOwnEditions(tournamentId, {
            name,
            size: 10,
            sort: "updated_at,DESC",
            tournament_id: tournamentId,
            status__in: ["UPCOMING", "ONGOING", "ENDED"]
        }).then(({ content }: SanitizedResponse<ITournamentEdition>) => {
            setTournamentEditions(content);
        }).catch(BaseService.reportError);
    }

}

export interface LandingProps {
    publicId: string;
    basePath: string;
    website: ITournamentEditionWebsite;
}

function Landing(props: LandingProps) {

    const mainLabels = i18nManager.Labels;
    const today = DateHelper.getStartOfDay();
    const { website, basePath, publicId } = props;
    const ddRef = React.useRef<HTMLDivElement>(null);
    const [cart, setCart] = React.useState<any[]>([]);
    const [showDD, setShowDD] = React.useState<boolean>(false);
    const nameSplit = website.tournament_edition.name.split(/\s+/);
    const [nameFirstWord, ...nameRemain] = nameSplit;
    const [showDropdown, setShowDropdown] = React.useState<boolean>(false);
    const [showRegister, setShowRegister] = React.useState<boolean>(false);
    const [searchParams, _] = ReactRouterDom.useSearchParams(window.location.search);
    const [profile, setProfile] = React.useState<IMember | undefined>(CacheManager.get(CacheManager.PROFILE_KEY) as IMember);
    const colors = searchParams.get("ds_colors")?.length ? JSON.parse(searchParams.get("ds_colors") ?? "{}") : (website.design_settings?.scheme_settings?.colors ?? {});
    useComponentStyle(`
          :root {
              --red: ${colors.red ?? "#EF4444"};
              --dark: ${colors.dark ?? "#0A0A0F"};
              --text: ${colors.text ?? "#E8E8F0"};
              --gold: ${colors.gold ?? "#C9A84C"};
              --green: ${colors.green ?? "#22C55E"};
              --dark2: ${colors.dark2 ?? "#111118"};
              --dark3: ${colors.dark3 ?? "#1A1A24"};
              --dark4: ${colors.dark4 ?? "#22222E"};
              --muted: ${colors.muted ?? "#7A7A96"};
              --accent: ${colors.accent ?? "#4F6EF7"};
              --orange: ${colors.orange ?? "#F97316"};
              --gold-light: ${colors.gold_light ?? "#E8C96A"};
              --header-h: 72px;
          }
      `);

    React.useEffect(() => {
        Utils.updateMeta({
            title: website.tournament_edition?.name,
            desc: website.tournament_edition?.description,
            logo: website.tournament_edition?.logo_url ?? website?.media?.LOGO?.[0]
        });
    }, [website]);

    React.useEffect(() => {
        const h = (e: MouseEvent) => { if (ddRef.current && !ddRef.current.contains(e.target as Node)) setShowDD(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    return (
        <>
            {/* ── HEADER ── */}
            <header className={styles["hdr"]}>
                <ReactRouterDom.NavLink to={{ pathname: `/${basePath}`, search: "edi=" + searchParams.get("edi") }} className={styles["hdr-logo-wrap"]} style={{ textDecoration: "none" }}>
                    <img className={styles["hdr-org-img"]} src={website.tournament_edition?.logo_url ?? website?.media?.LOGO?.[0]} alt={website.tournament_edition.name} />
                    <div className={styles["hdr-logo-txt"]}>{nameFirstWord} <span>{nameRemain.join(" ")}</span><span className={styles["logo-pill"]}>{nameSplit.map((s) => s[0]).join("")}</span></div>
                </ReactRouterDom.NavLink>

                <EditionSwitcher tournamentId={website.tournament_edition.tournament_id} current={website.tournament_edition} />

                <nav className={styles["hdr-nav"]}>
                    {Utils.entriesInCustomOrder(website.website_pages, ["HOME", "MERCH", "SCHEDULE", "PARTICIPANTS", "LEADERBOARD", "TROPHIES", "EVENTS", "GALLERY", "ABOUT"]).filter(([_, pageSettings]) => pageSettings.visible).map(([page, pageSettings]) => (
                        <ReactRouterDom.NavLink end key={page} to={{ pathname: `/${basePath}${page === "HOME" ? "" : "/" + page.toLowerCase()}`, search: searchParams.toString() }} className={({ isActive }) => Classname.build(styles[`nav-btn`], { [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                    ))}
                </nav>

                <div className={styles["hdr-right"]}>
                    {cart.length > 0 && <span style={{ fontSize: "0.72rem", color: "var(--gold)" }}>🛒{cart.length}</span>}
                    <LanguageDropdown />
                    {(website.extra_data.enable_visitor_account_management) && ((!profile) ? (
                        <button className={styles["btn-signin"]} onClick={() => Utils.signIn()}>Sign In</button>
                    ) : (
                        <div className={styles["dropdown-wrap"]} ref={ddRef}>
                            <img className={styles["avatar-btn"]} onClick={() => setShowDropdown((v: boolean) => !v)} src={profile.profile_picture_url} alt={profile.username} />
                            {showDropdown && (
                                <div className={styles["dropdown"]}>
                                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}`} className={({ }) => Classname.build(styles["dd-item"])}>📊 Dashboard</ReactRouterDom.NavLink>
                                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}/teams`} className={({ }) => Classname.build(styles["dd-item"])}>⚙️ My Teams</ReactRouterDom.NavLink>
                                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}/tournaments?section=REGISTERED`} className={({ }) => Classname.build(styles["dd-item"])}>🏆 My Tournaments</ReactRouterDom.NavLink>
                                    <ReactRouterDom.NavLink target='_blank' to={`${process.env.REACT_APP_OMNEHOST_SUPPORT_URL}`} className={({ }) => Classname.build(styles["dd-item"])}>🎧 Support</ReactRouterDom.NavLink>
                                    <hr className={styles["dd-divider"]} />
                                    <button className={`${styles["dd-item"]} ${styles["logout"]}`} onClick={() => {
                                        CacheManager.clear();
                                        setProfile(undefined);
                                    }}>← Sign Out</button>
                                </div>
                            )}
                        </div>
                    ))}
                    {website.tournament_edition.status === "UPCOMING" && (<button className={styles["btn-register-hdr"]} onClick={() => setShowRegister(true)}>Register →</button>)}
                </div>
            </header>

            <main className={styles["page"]}>
                <ReactRouterDom.Routes>
                    <ReactRouterDom.Route path="/events" element={<Events website={website} />} />
                    <ReactRouterDom.Route path="/gallery" element={<Gallery website={website} />} />
                    <ReactRouterDom.Route path="/trophies" element={<Trophies website={website} />} />
                    <ReactRouterDom.Route path="/leaderboard" element={<Leaderboard website={website} />} />
                    <ReactRouterDom.Route path="/participants" element={<Participants website={website} />} />
                    <ReactRouterDom.Route path="/about" element={<About website={website} basePath={basePath} />} />
                    <ReactRouterDom.Route path="/schedule" element={<Schedule publicId={publicId} website={website} basePath={basePath} />} />
                </ReactRouterDom.Routes>
            </main>

            {/* ── FOOTER ── */}
            <footer className={styles["footer"]}>
                <div className={styles["footer-inner"]}>
                    <div>
                        <div className={styles["footer-logo"]}>{website.owns_copyright ? website.tournament_edition.name : mainLabels.omnehost}</div>
                        <p className={styles["footer-desc"]}>
                            {website.owns_copyright ? website.tournament_edition.description : mainLabels.omnehost_detail}
                        </p>
                    </div>
                    <div>
                        <div className={styles["f-col-title"]}>Navigation</div>
                        <ul className={styles["f-links"]}>
                            {Utils.entriesInCustomOrder(website.website_pages).filter(([_, pageSettings]) => pageSettings.visible).map(([page, pageSettings]) => (<li key={page}>
                                <ReactRouterDom.NavLink end to={{ pathname: `/${basePath}${page === "HOME" ? "" : "/" + page.toLowerCase()}`, search: searchParams.toString() }} className={({ isActive }) => Classname.build({ [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                            </li>))}
                        </ul>
                    </div>
                    <div>
                        <div className={styles["f-col-title"]}>Connect</div>
                        <ul className={styles["f-links"]}>
                            {Object.entries(website.social_accounts ?? {}).map(([social, username]) => (<li key={social}>
                                <ReactRouterDom.NavLink target="_blank" to={Utils.getSocialLink(social, username)}>{StringHelper.toSaneSentenceFormat(social)}</ReactRouterDom.NavLink>
                            </li>))}
                        </ul>
                    </div>
                    {(!website.owns_copyright) && (<div>
                        <div className={styles["f-col-title"]}>{mainLabels.omnehost}</div>
                        <ul className={styles["f-links"]}>
                            <li>
                                {(!!profile)
                                    ? (<ReactRouterDom.NavLink target="_blank" to={process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}>{mainLabels.auth.dashboard}</ReactRouterDom.NavLink>)
                                    : (<ReactRouterDom.NavLink target="_blank" to={process.env.REACT_APP_OMNEHOST_DASHBOARD_URL ?? "http://127.0.0.1:3901"}>{mainLabels.auth.sign_in}</ReactRouterDom.NavLink>)}
                            </li>
                            {mainLabels.footer.map((f) => <li key={f.label}><ReactRouterDom.NavLink key={f.label} target="_blank" to={f.location}>{f.label}</ReactRouterDom.NavLink></li>)}
                        </ul>
                    </div>)}
                </div>
                <div className={styles["footer-bottom"]}>
                    <span>© {today.getFullYear()} {website.owns_copyright ? website.tournament_edition.name : mainLabels.omnehost}. {mainLabels.landing.all_r_reserved}</span>
                    <div className={styles["footer-socials"]}>
                        {Object.entries(website.owns_copyright ? (website.social_accounts ?? {}) : Utils.OMNEHOST_SOCIALS).map(([social, username]) => (
                            <ReactRouterDom.NavLink key={social} target="_blank" to={Utils.getSocialLink(social, username)} className={styles["f-soc-btn"]}><i className={Utils.getSocialIcon(social)} /></ReactRouterDom.NavLink>
                        ))}
                    </div>
                </div>
            </footer>
        </>
    )
}

export default Landing;